import * as React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router';
import { useSelector } from '../../../redux/store';
import EventEntryModal from '../../../views/home/components/eventEntryModal';
import ProfileModal from '../../../views/home/components/profileModal';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useMediaQuery, useTheme, Tooltip } from '@mui/material';

const Sidebar = () => {
  const username = useSelector(state => state.auth.username) ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const [eventModalOpen, setEventModalOpen] = React.useState(false);
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  
  // Responsive breakpoint - tablet boyutlarında sidebar'ı daralt
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md')); // md'den küçük ekranlarda compact mode

  const menuItems = [
    {
      title: 'Anasayfa',
      icon: <HomeIcon />,
      link: '/',
    },
    {
      title: 'Analizler',
      icon: <AnalyticsIcon />,
      link: '/analysis',
    },
    {
      title: 'Tesislerim',
      icon: <AssignmentIcon />,
      link: '/reports',
    },
    {
      title: 'Olay Bildir',
      icon: <AddCircleOutlineIcon />,
      action: () => setEventModalOpen(true),
    },
    {
      title: 'Ayarlar',
      icon: <SettingsIcon />,
      action: () => setProfileModalOpen(true),
    },
  ];

  return (
    <Box
      sx={{
        width: isCompact ? 64 : 220,
        height: '100vh',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
        transition: 'width 0.3s ease',
      }}
    >
      <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
        {isCompact ? (
          <Box sx={{ 
            minHeight: 48,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            px: 2.5
          }}>
            <img
              src="/logo_inttech.png"
              alt="Int-Tech Logo"
              style={{
                width: '24px',
                height: '24px',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            minHeight: 64,
            display: 'flex', 
            alignItems: 'center',
            px: 2,
            gap: 1.5
          }}>
            <img
              src="/logo_inttech.png"
              alt="Int-Tech Logo"
              style={{
                width: '24px',
                height: '24px',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: 'Conthrax, monospace',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: 'primary.main',
              }}
            >
              INT-TECH
            </Typography>
          </Box>
        )}
      </Box>

      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => (
          <ListItem key={item.title} disablePadding>
            {isCompact ? (
              <Tooltip title={item.title} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (item.action) {
                      item.action();
                    } else if (item.link) {
                      navigate(item.link);
                    }
                  }}
                  selected={item.link === location.pathname}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'center',
                    px: 2.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton
                onClick={() => {
                  if (item.action) {
                    item.action();
                  } else if (item.link) {
                    navigate(item.link);
                  }
                }}
                selected={item.link === location.pathname}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                  },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>

      <Box sx={{ 
        p: isCompact ? 1 : 2, 
        borderTop: '1px solid #e0e0e0',
        marginTop: 'auto'
      }}>
        {isCompact ? (
          <Tooltip title="Çıkış" placement="right">
            <ListItemButton
              onClick={() => navigate('/logout')}
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 2.5,
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                <LogoutIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={() => navigate('/logout')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Çıkış" />
          </ListItemButton>
        )}
      </Box>

      <EventEntryModal 
        open={eventModalOpen} 
        onClose={() => setEventModalOpen(false)} 
      />
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </Box>
  );
};

export default Sidebar; 